import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/shared/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/components/dialog';
import { Label } from '@/shared/ui/components/label';
import { useAuthorization } from '@/modules/auth/useAuthorization';
import { useVenueContext } from '@/modules/venue/VenueContext';
import { Forbidden } from '@/modules/auth/Forbidden';
import { apiErrorMessage } from '@/shared/api-client/workspace';
import {
  useAddVariant,
  useCreateProduct,
  useEditVariant,
  useInventoryItems,
  type InventoryItem,
  type InventoryVariant,
  type Money,
} from './inventory';

const FIELD_CLASS = 'h-9 rounded-md border border-input bg-transparent px-3 text-sm shadow-xs';

function formatMoney(money?: Money): string {
  if (!money) return '—';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: money.currency }).format(
      money.amount,
    );
  } catch {
    return `${money.amount} ${money.currency}`;
  }
}

/** Margin as 100 − cost / price * 100%; — when either side is missing or price is zero. */
function marginPct(variant: InventoryVariant): string {
  const price = variant.price?.amount;
  const cogs = variant.cogs?.amount;
  if (price == null || cogs == null || price === 0) return '—';
  return `${(100 - (cogs / price) * 100).toFixed(1)}%`;
}

const num = (value: string): number | undefined =>
  value.trim() === '' ? undefined : Number(value);

/**
 * Venue-workspace Inventory page (`/loc/:venueId/inventory`). Lists the current Venue's
 * InventoryItemVariants (one row per variant) with a Create-product action and a per-row Edit action.
 */
export function InventoryPage() {
  const { t } = useTranslation('common');
  const auth = useAuthorization();
  const { venueId } = useVenueContext();
  const scope = { kind: 'venue', venueId } as const;
  const canView = auth.canAny(['products.inventory.view', 'products.inventory.manage'], scope);
  const canManage = auth.can('products.inventory.manage', scope);

  const { data, isLoading, isError } = useInventoryItems(venueId);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<{ item: InventoryItem; variant: InventoryVariant } | null>(
    null,
  );

  if (!canView) return <Forbidden />;

  const term = search.trim().toLowerCase();
  const rows = (data ?? [])
    .flatMap((item) => item.variants.map((variant) => ({ item, variant })))
    .filter(({ item, variant }) =>
      term === '' ? true : `${item.name} ${variant.name}`.toLowerCase().includes(term),
    );

  return (
    <section className="grid gap-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold">{t('inventory.title')}</h1>
        <div className="flex items-center gap-3">
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('inventory.searchPlaceholder')}
            className="h-9 w-64 rounded-md border border-input bg-transparent px-3 text-sm shadow-xs"
          />
          {canManage && (
            <Button onClick={() => setCreating(true)}>{t('inventory.actions.create')}</Button>
          )}
        </div>
      </div>

      {isLoading && <p className="text-muted-foreground">{t('inventory.loading')}</p>}
      {isError && <p className="text-destructive">{t('inventory.errors.loadFailed')}</p>}

      {data && (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/50 text-left">
              <tr>
                <th className="px-4 py-2 font-medium">{t('inventory.columns.productGroup')}</th>
                <th className="px-4 py-2 font-medium">{t('inventory.columns.productName')}</th>
                <th className="px-4 py-2 font-medium">{t('inventory.columns.quantity')}</th>
                <th className="px-4 py-2 font-medium">{t('inventory.columns.price')}</th>
                <th className="px-4 py-2 font-medium">{t('inventory.columns.cogs')}</th>
                <th className="px-4 py-2 font-medium">{t('inventory.columns.margin')}</th>
                <th className="px-4 py-2 font-medium">{t('inventory.columns.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-muted-foreground">
                    {t('inventory.empty')}
                  </td>
                </tr>
              )}
              {rows.map(({ item, variant }) => (
                <tr key={variant.id} className="border-b last:border-0">
                  <td className="px-4 py-2 font-medium">{item.name}</td>
                  <td className="px-4 py-2">{variant.name}</td>
                  <td className="px-4 py-2">{variant.quantity ?? '—'}</td>
                  <td className="px-4 py-2">{formatMoney(variant.price)}</td>
                  <td className="px-4 py-2">{formatMoney(variant.cogs)}</td>
                  <td className="px-4 py-2">{marginPct(variant)}</td>
                  <td className="px-4 py-2">
                    {canManage && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditing({ item, variant })}
                      >
                        {t('inventory.actions.edit')}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateProductDialog
        open={creating}
        venueId={venueId}
        items={data ?? []}
        onOpenChange={setCreating}
        onCreated={() => toast.success(t('inventory.toast.created'))}
      />
      <EditVariantDialog
        venueId={venueId}
        editing={editing}
        onOpenChange={(open) => !open && setEditing(null)}
        onSaved={() => toast.success(t('inventory.toast.updated'))}
      />
    </section>
  );
}

function CreateProductDialog({
  open,
  venueId,
  items,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  venueId: string;
  items: InventoryItem[];
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation('common');
  const createProduct = useCreateProduct(venueId);
  const addVariant = useAddVariant(venueId);
  const [groupName, setGroupName] = useState('');
  const [groupDescription, setGroupDescription] = useState('');
  const [productName, setProductName] = useState('');
  const [quantity, setQuantity] = useState('');
  const [price, setPrice] = useState('');
  const [cogs, setCogs] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setGroupName('');
      setGroupDescription('');
      setProductName('');
      setQuantity('');
      setPrice('');
      setCogs('');
      setError(null);
    }
  }, [open]);

  // Resolve the typed group to an existing one (case-insensitive); no match means a new group.
  const existingGroup = items.find(
    (item) => item.name.trim().toLowerCase() === groupName.trim().toLowerCase(),
  );
  const descriptionValue = existingGroup ? (existingGroup.description ?? '') : groupDescription;
  const pending = createProduct.isPending || addVariant.isPending;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      if (existingGroup) {
        await addVariant.mutateAsync({
          itemId: existingGroup.id,
          input: {
            name: productName,
            quantity: num(quantity),
            price: Number(price),
            cogs: num(cogs),
          },
        });
      } else {
        await createProduct.mutateAsync({
          groupName,
          groupDescription,
          productName,
          quantity: num(quantity),
          price: Number(price),
          cogs: num(cogs),
        });
      }
      onOpenChange(false);
      onCreated();
    } catch (caught) {
      setError(apiErrorMessage(t, 'inventory', caught));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{t('inventory.create.title')}</DialogTitle>
            <DialogDescription>{t('inventory.create.description')}</DialogDescription>
          </DialogHeader>

          <div className="grid gap-2">
            <Label htmlFor="inv-group">{t('inventory.fields.group')}</Label>
            <input
              id="inv-group"
              list="inv-group-options"
              value={groupName}
              onChange={(event) => setGroupName(event.target.value)}
              placeholder={t('inventory.fields.groupPlaceholder')}
              className={FIELD_CLASS}
            />
            <datalist id="inv-group-options">
              {items.map((item) => (
                <option key={item.id} value={item.name} />
              ))}
            </datalist>
            {existingGroup && (
              <p className="text-xs text-muted-foreground">{t('inventory.create.existingGroup')}</p>
            )}
          </div>
          <div className="grid gap-2">
            <Label htmlFor="inv-group-description">{t('inventory.fields.groupDescription')}</Label>
            <textarea
              id="inv-group-description"
              value={descriptionValue}
              onChange={(event) => setGroupDescription(event.target.value)}
              disabled={Boolean(existingGroup)}
              rows={2}
              className="min-h-16 rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-xs disabled:opacity-60"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="inv-product">{t('inventory.fields.product')}</Label>
            <input
              id="inv-product"
              value={productName}
              onChange={(event) => setProductName(event.target.value)}
              className={FIELD_CLASS}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="inv-quantity">{t('inventory.fields.quantity')}</Label>
            <input
              id="inv-quantity"
              type="number"
              step="1"
              min="0"
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
              className={FIELD_CLASS}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="inv-price">{t('inventory.fields.price')}</Label>
              <input
                id="inv-price"
                type="number"
                step="0.01"
                min="0"
                required
                value={price}
                onChange={(event) => setPrice(event.target.value)}
                className={FIELD_CLASS}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="inv-cogs">{t('inventory.fields.cogs')}</Label>
              <input
                id="inv-cogs"
                type="number"
                step="0.01"
                min="0"
                value={cogs}
                onChange={(event) => setCogs(event.target.value)}
                className={FIELD_CLASS}
              />
            </div>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('actions.cancel')}
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? t('inventory.create.submitting') : t('inventory.create.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditVariantDialog({
  venueId,
  editing,
  onOpenChange,
  onSaved,
}: {
  venueId: string;
  editing: { item: InventoryItem; variant: InventoryVariant } | null;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation('common');
  const editVariant = useEditVariant(venueId);
  const open = editing !== null;
  const [name, setName] = useState('');
  const [quantity, setQuantity] = useState('');
  const [price, setPrice] = useState('');
  const [cogs, setCogs] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (editing) {
      setName(editing.variant.name);
      setQuantity(editing.variant.quantity?.toString() ?? '');
      setPrice(editing.variant.price?.amount?.toString() ?? '');
      setCogs(editing.variant.cogs?.amount?.toString() ?? '');
      setError(null);
    }
  }, [editing]);

  if (!editing) return null;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    try {
      await editVariant.mutateAsync({
        itemId: editing!.item.id,
        variantId: editing!.variant.id,
        input: { name, quantity: num(quantity), price: Number(price), cogs: num(cogs) },
      });
      onOpenChange(false);
      onSaved();
    } catch (caught) {
      setError(apiErrorMessage(t, 'inventory', caught));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={submit} className="grid gap-4">
          <DialogHeader>
            <DialogTitle>{t('inventory.edit.title')}</DialogTitle>
            <DialogDescription>{editing.item.name}</DialogDescription>
          </DialogHeader>

          <div className="grid gap-2">
            <Label htmlFor="inv-edit-name">{t('inventory.fields.product')}</Label>
            <input
              id="inv-edit-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              className={FIELD_CLASS}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="inv-edit-quantity">{t('inventory.fields.quantity')}</Label>
            <input
              id="inv-edit-quantity"
              type="number"
              step="1"
              min="0"
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
              className={FIELD_CLASS}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="inv-edit-price">{t('inventory.fields.price')}</Label>
              <input
                id="inv-edit-price"
                type="number"
                step="0.01"
                min="0"
                required
                value={price}
                onChange={(event) => setPrice(event.target.value)}
                className={FIELD_CLASS}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="inv-edit-cogs">{t('inventory.fields.cogs')}</Label>
              <input
                id="inv-edit-cogs"
                type="number"
                step="0.01"
                min="0"
                value={cogs}
                onChange={(event) => setCogs(event.target.value)}
                className={FIELD_CLASS}
              />
            </div>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('actions.cancel')}
            </Button>
            <Button type="submit" disabled={editVariant.isPending}>
              {editVariant.isPending ? t('inventory.edit.submitting') : t('inventory.edit.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
